import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ArrowLeft, Reply, Forward, Trash2, MailIcon, Send, X, Image, ImageOff, Paperclip, ExternalLink, PenSquare, Download, Play, FileIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { SelectedEmail } from "./chat-list-panel";
import type { EmailAccount } from "../email-drawer";
import { getEmailSettings } from "../email-settings-dialog";
import { useMobyNodeClient, useMobyDB, useSession, useFileTransfer } from "../provider";
import { getEmailAccountsFromDB, refreshOAuth2TokenIfNeeded } from "@/services/email-account-store";
import { FileTransferStateEnum } from "@/lib/file-transfer-types";
import { liveQuery } from "dexie";
import { createAndSignContactDetails } from "@/moby/repo";
import { bytesToHex, hexToBytes } from "@noble/curves/abstract/utils";
import { ed25519 } from "@noble/curves/ed25519";

/**
 * Client-side MIME body extractor — detects raw multipart MIME in email body
 * and extracts the readable text/plain or text/html content.
 * Handles base64 and quoted-printable Content-Transfer-Encoding.
 */
function extractReadableBody(raw: string): { text: string; html: string | null } {
  // Detect MIME boundary
  const boundaryMatch = raw.match(/--([^\r\n\s]+)/);
  if (!boundaryMatch) return { text: raw, html: null };

  const boundary = boundaryMatch[1];
  // Split on boundary (handle both -- prefix forms)
  const parts = raw.split(new RegExp(`--${boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));

  let plainText = "";
  let htmlText: string | null = null;

  for (const part of parts) {
    if (!part.trim() || part.trim() === "--") continue;

    // Parse headers from this MIME part
    const headerEnd = part.indexOf("\r\n\r\n") !== -1
      ? part.indexOf("\r\n\r\n")
      : part.indexOf("\n\n");
    if (headerEnd === -1) continue;

    const headers = part.slice(0, headerEnd).toLowerCase();
    const body = part.slice(headerEnd).replace(/^[\r\n]+/, "");

    const isPlain = headers.includes("text/plain");
    const isHtml = headers.includes("text/html");
    if (!isPlain && !isHtml) continue;

    let decoded = body;

    if (headers.includes("base64")) {
      try {
        // Strip whitespace and decode base64
        const clean = body.replace(/[\r\n\s]/g, "");
        decoded = decodeURIComponent(
          atob(clean).split("").map(c =>
            "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
          ).join("")
        );
      } catch {
        decoded = body; // fallback to raw if decode fails
      }
    } else if (headers.includes("quoted-printable")) {
      decoded = body
        .replace(/=\r?\n/g, "") // soft line breaks
        .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
    }

    if (isPlain && !plainText) plainText = decoded.trim();
    if (isHtml && !htmlText) htmlText = decoded.trim();
  }

  return {
    text: plainText || raw, // fall back to original if extraction failed
    html: htmlText,
  };
}

/** Check if a string looks like raw MIME multipart content */
function looksLikeRawMime(body: string): boolean {
  if (!body) return false;
  const trimmed = body.trimStart();
  return (
    trimmed.startsWith("--") &&
    (trimmed.includes("Content-Type:") || trimmed.includes("content-type:"))
  );
}

// Base64 encoding compatible with tweetnacl-util's decodeBase64
function encodeBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Authenticated fetch to email gateway (signs request with ed25519 key)
async function signedGatewayFetch(
  url: string,
  body: Record<string, unknown>,
  mobyAddress: string,
  mobySecret: string
): Promise<Response> {
  const timestamp = Date.now().toString();
  const requestBody = JSON.stringify(body);
  const messageToSign = `${timestamp}${requestBody}`;
  const messageBytes = new TextEncoder().encode(messageToSign);
  const signature = ed25519.sign(messageBytes, hexToBytes(mobySecret));
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Moby-Address": encodeBase64(hexToBytes(mobyAddress)),
      "X-Moby-Timestamp": timestamp,
      "X-Moby-Signature": encodeBase64(signature),
    },
    body: requestBody,
  });
}

// Authenticated GET to email gateway (signs timestamp-only since no body)
async function signedGatewayGet(
  url: string,
  mobyAddress: string,
  mobySecret: string
): Promise<Response> {
  const timestamp = Date.now().toString();
  const messageBytes = new TextEncoder().encode(timestamp);
  const signature = ed25519.sign(messageBytes, hexToBytes(mobySecret));
  return fetch(url, {
    method: "GET",
    headers: {
      "X-Moby-Address": encodeBase64(hexToBytes(mobyAddress)),
      "X-Moby-Timestamp": timestamp,
      "X-Moby-Signature": encodeBase64(signature),
    },
  });
}

// Legacy localStorage fallback — used only when IndexedDB is unavailable
function getEmailAccountsFromLocalStorage(): EmailAccount[] {
  try {
    const stored = localStorage.getItem("moby_pref_emailAccounts");
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

interface RecipientLookup {
  status: "idle" | "checking" | "found" | "not_found";
  user?: {
    publicKey: string;
    preferredName?: string;
    presence?: { status: string };
  };
}

/**
 * Sanitize HTML content for safe rendering
 * @param html - Raw HTML string from email
 * @param allowImages - Whether to allow image loading
 * @returns Sanitized HTML string wrapped in basic document structure
 */
function sanitizeHtmlForIframe(html: string, allowImages: boolean): string {
  let sanitized = html;
  
  // Remove potentially dangerous elements
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
  sanitized = sanitized.replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '');
  sanitized = sanitized.replace(/<embed[^>]*>/gi, '');
  sanitized = sanitized.replace(/<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, 'data-removed=');
  
  if (!allowImages) {
    // Remove or replace images with placeholders
    sanitized = sanitized.replace(/<img[^>]*>/gi, '<span style="color:#888;font-style:italic">[Image blocked]</span>');
    // Block background images in styles
    sanitized = sanitized.replace(/background(-image)?\s*:\s*url\([^)]*\)/gi, 'background:none');
  }
  
  // Wrap in basic HTML document with base styles
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: #1a1a1a;
      margin: 0;
      padding: 16px;
      word-wrap: break-word;
    }
    img { max-width: 100%; height: auto; }
    a { color: #0066cc; }
    table { max-width: 100%; }
    pre, code { white-space: pre-wrap; word-wrap: break-word; }
  </style>
</head>
<body>${sanitized}</body>
</html>`;
}

interface EmailAttachment {
  filename: string;
  contentType: string;
  size: number;
  partIndex: number;
  contentId?: string;
  disposition: string;
}

interface EmailContent {
  uid: number;
  from: string;
  fromName: string;
  to: Array<{ address: string; name: string }>;
  cc: Array<{ address: string; name: string }>;
  subject: string;
  date: string;
  body: string;
  htmlBody: string;
  messageId: string;
  inReplyTo: string;
  attachments?: EmailAttachment[];
}

interface EmailViewPanelProps {
  selectedEmail?: SelectedEmail | null;
  onBack: () => void;
  onCompose?: () => void;
  composeOnly?: boolean;
}

export function EmailViewPanel({ selectedEmail, onBack, composeOnly = false }: EmailViewPanelProps) {
  // P2P file transfer hooks
  const node = useMobyNodeClient();
  const db = useMobyDB();
  const session = useSession();
  const fileTransfer = useFileTransfer();
  
  const [loading, setLoading] = useState(!composeOnly);
  const [error, setError] = useState<string | null>(null);
  const [emailContent, setEmailContent] = useState<EmailContent | null>(null);
  const [replyMode, setReplyMode] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadImages, setLoadImages] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // P2P file preparation progress (Merkle tree + OPFS write)
  const [prepProgress, setPrepProgress] = useState<{ phase: string; progress: number; fileName: string } | null>(null);

  // =====================================================================
  // FILE PREPARATION PROGRESS — listen for moby:transfer-progress events
  // dispatched by prepareEmailTransfer during Merkle tree (0-50%) and
  // OPFS write (50-100%). Updates the send button spinner with progress.
  // =====================================================================
  useEffect(() => {
    if (!sending) {
      setPrepProgress(null);
      return;
    }

    const handleTransferProgress = (event: Event) => {
      const { progress, phase, fileName } = (event as CustomEvent).detail;
      if (!phase || !fileName) return;

      if (phase === 'complete') {
        setPrepProgress({ phase: 'complete', progress: 100, fileName });
      } else {
        setPrepProgress({ phase, progress: Math.round(progress), fileName });
      }
    };

    window.addEventListener('moby:transfer-progress', handleTransferProgress);
    return () => {
      window.removeEventListener('moby:transfer-progress', handleTransferProgress);
    };
  }, [sending]);

  // =====================================================================
  // EMAIL TRANSFER TOAST FEEDBACK — mirrors the chat panel gold standard.
  // Uses stable toast ID (sessionId) so toasts update in-place through the
  // full lifecycle: queued → merkle → opfs → complete → consent.
  // =====================================================================
  useEffect(() => {
    // Preparation lifecycle — same events as chat panel (moby:transfer-progress)
    const handleTransferProgress = (event: Event) => {
      const { sessionId, progress, phase, fileName } = (event as CustomEvent).detail;
      if (!sessionId || !fileName) return;

      switch (phase) {
        case 'preparing':
        case 'queued':
          toast.loading(`Preparing ${fileName}...`, { id: sessionId });
          break;
        case 'merkle':
          toast.loading(`Building merkle tree... ${Math.round(progress)}%`, { id: sessionId });
          break;
        case 'opfs':
          toast.loading(`Writing to storage... ${Math.round(progress)}%`, { id: sessionId });
          break;
        case 'complete':
          toast.success(`${fileName} ready for transfer`, { id: sessionId });
          break;
      }
    };

    // Email-specific: queued event fires immediately on send (before progress starts)
    const handleQueued = (event: Event) => {
      const { sessionId, fileName } = (event as CustomEvent).detail;
      if (sessionId && fileName) {
        toast.loading(`Preparing ${fileName}...`, { id: sessionId });
      }
    };

    // Email-specific: ready event fires when consent phase begins
    const handleReady = (event: Event) => {
      const { sessionId, fileName, emailRecipient } = (event as CustomEvent).detail;
      toast.success(`${fileName} ready for transfer`, {
        id: sessionId,
        description: `Awaiting ${emailRecipient || 'recipient'} consent`
      });
    };

    window.addEventListener('moby:transfer-progress', handleTransferProgress);
    window.addEventListener('moby:email-transfer-queued', handleQueued);
    window.addEventListener('moby:email-transfer-ready', handleReady);
    return () => {
      window.removeEventListener('moby:transfer-progress', handleTransferProgress);
      window.removeEventListener('moby:email-transfer-queued', handleQueued);
      window.removeEventListener('moby:email-transfer-ready', handleReady);
    };
  }, []);

  // IMAP inline attachment download state
  const [downloadingParts, setDownloadingParts] = useState<Set<number>>(new Set());

  const handleDownloadAttachment = useCallback(async (att: EmailAttachment) => {
    if (!selectedEmail || downloadingParts.has(att.partIndex)) return;
    const gatewayUrl = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
    if (!gatewayUrl) { toast.error("Email gateway not configured"); return; }
    const account = selectedEmail.account;
    const credentials: { user: string; pass?: string; accessToken?: string } = { user: account.username };
    if (account.authType === "oauth2" && account.accessToken) {
      credentials.accessToken = account.accessToken;
    } else {
      credentials.pass = account.password;
    }
    setDownloadingParts(prev => new Set(prev).add(att.partIndex));
    try {
      const resp = await fetch(`${gatewayUrl}/api/email/imap/fetch-attachment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host: account.imapHost, port: account.imapPort, secure: true, credentials,
          folder: "INBOX", uid: selectedEmail.uid, partIndex: att.partIndex,
        }),
      });
      const data = await resp.json();
      if (data.success && data.attachment?.data) {
        const byteChars = atob(data.attachment.data);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArray], { type: data.attachment.contentType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = data.attachment.filename; a.click();
        URL.revokeObjectURL(url);
        toast.success(`Downloaded ${data.attachment.filename}`);
      } else {
        toast.error(data.error || "Failed to download attachment");
      }
    } catch (err) {
      toast.error("Failed to download attachment");
    } finally {
      setDownloadingParts(prev => { const s = new Set(prev); s.delete(att.partIndex); return s; });
    }
  }, [selectedEmail, downloadingParts]);

  // Compose mode state - start in compose mode if composeOnly
  const [composeMode, setComposeMode] = useState(composeOnly);
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeFromAccountId, setComposeFromAccountId] = useState<string>("");
  const [composeAttachments, setComposeAttachments] = useState<File[]>([]);
  const [attachmentRouting, setAttachmentRouting] = useState<"fileTransfer" | "inline">("fileTransfer");
  const [recipientLookup, setRecipientLookup] = useState<RecipientLookup>({ status: "idle" });
  const lookupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Load email accounts from IndexedDB (wallet-scoped, async)
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  useEffect(() => {
    let cancelled = false;
    getEmailAccountsFromDB(db).then(loaded => {
      if (!cancelled) setAccounts(loaded);
    });
    // Re-load when preferences are updated (e.g. drawer saves new account)
    const handlePrefsUpdate = () => {
      getEmailAccountsFromDB(db).then(loaded => {
        if (!cancelled) setAccounts(loaded);
      });
    };
    window.addEventListener('user-preferences-updated', handlePrefsUpdate);
    return () => { cancelled = true; window.removeEventListener('user-preferences-updated', handlePrefsUpdate); };
  }, [db]);

  // Gateway-sourced pending file deliveries — pre-populate UI before P2P connects
  const [gatewayPendingFiles, setGatewayPendingFiles] = useState<Array<{
    fileName: string;
    fileSize: number;
    senderAddress: string;
    emailSubject?: string;
  }>>([]);

  // DB-backed email transfer records — survive refresh, onboarding, reconnect
  const [dbEmailTransfers, setDbEmailTransfers] = useState<Array<{
    sessionId: string;
    fileName: string;
    fileSize: number;
    state: string;
    direction: string;
    senderAddress: string;
    recipientEmail: string;
    emailSubject?: string;
  }>>([]);

  // Reactively load email transfer records from DB using Dexie liveQuery
  // Auto-updates when records are written/updated (e.g., after peer connects post-onboarding)
  // For outbound: recipientEmail = the TO address
  // For inbound: recipientEmail = OUR email (not sender's), so we load all and filter
  useEffect(() => {
    if (composeOnly || !emailContent?.from) {
      setDbEmailTransfers([]);
      return;
    }
    const viewedEmail = emailContent.from.toLowerCase();
    const viewedSubject = emailContent.subject || '';
    const ourEmails = new Set(accounts.map(a => a.email.toLowerCase()));

    const subscription = liveQuery(() => db.emailTransfers.toArray()).subscribe({
      next: (allRecords) => {
        const relevant = allRecords.filter(r => {
          // Direction filter: outbound to this peer OR inbound to us
          const dirMatch = 
            (r.direction === 'outbound' && r.recipientEmail === viewedEmail) ||
            (r.direction === 'inbound' && ourEmails.has(r.recipientEmail));
          if (!dirMatch) return false;
          // Subject scoping: match emailSubject to the viewed email's subject
          // Legacy records without emailSubject are included for backward compat
          if (r.emailSubject && viewedSubject) {
            return r.emailSubject === viewedSubject;
          }
          return true; // no subject stored → show (legacy)
        });
        setDbEmailTransfers(relevant.map(r => ({
          sessionId: r.sessionId,
          fileName: r.fileName,
          fileSize: r.fileSize,
          state: r.state,
          direction: r.direction,
          senderAddress: r.senderAddress,
          recipientEmail: r.recipientEmail,
          emailSubject: r.emailSubject,
        })));
        console.log('[EmailViewPanel] DB email transfers (live):', {
          viewedEmail,
          viewedSubject,
          count: relevant.length,
          records: relevant.map(r => ({ sid: r.sessionId.slice(0, 8), file: r.fileName, state: r.state, dir: r.direction, subj: r.emailSubject }))
        });
      },
      error: (err) => {
        console.debug('[EmailViewPanel] liveQuery error:', err);
      },
    });

    return () => subscription.unsubscribe();
  }, [db, emailContent?.from, emailContent?.subject, composeOnly]);

  // FIRST-OPEN SYNC: Query gateway for pending file deliveries before P2P connects.
  // This ensures attachment cards appear immediately when viewing an email, even if
  // the sender's deferred delivery hasn't been activated via P2P yet.
  useEffect(() => {
    if (composeOnly || !emailContent?.from || !emailContent?.subject) {
      setGatewayPendingFiles([]);
      return;
    }
    // Only query if we have no DB records yet (gateway is lowest-priority source)
    if (dbEmailTransfers.length > 0) return;

    const gatewayUrl = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
    if (!gatewayUrl || !session?.address || !session?.secret) return;

    // Find our registered email to query by
    const ourEmails = accounts.map(a => a.email.toLowerCase());
    if (ourEmails.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const ourEmail of ourEmails) {
        if (cancelled) return;
        try {
          const params = new URLSearchParams({
            email: ourEmail,
            subject: emailContent.subject,
          });
          const resp = await signedGatewayGet(
            `${gatewayUrl}/api/email/pending/by-email?${params}`,
            session.address,
            session.secret
          );
          if (!resp.ok || cancelled) continue;
          const data = await resp.json();
          if (cancelled) return;
          if (data.success && Array.isArray(data.pending) && data.pending.length > 0) {
            setGatewayPendingFiles(data.pending.map((p: any) => ({
              fileName: p.metadata?.fileName || 'Unknown file',
              fileSize: p.metadata?.fileSize || 0,
              senderAddress: p.senderAddress || '',
              emailSubject: p.metadata?.emailSubject,
            })));
            console.log('[EmailViewPanel] Gateway pending files:', data.pending.length);
            return; // Found results, stop checking other emails
          }
        } catch (err) {
          console.debug('[EmailViewPanel] Gateway pending files query error:', err);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [composeOnly, emailContent?.from, emailContent?.subject, session?.address, dbEmailTransfers.length]);

  // Detect pending inbound email P2P transfers for this email's sender
  // Merges in-memory transfers with DB-persisted records for resilience
  const pendingEmailTransfers = useMemo(() => {
    if (!emailContent?.from || composeOnly) return [];
    
    const senderEmail = emailContent.from.toLowerCase();
    const transfers: Array<{
      sessionId: string;
      fileName: string;
      fileSize: number;
      state: FileTransferStateEnum;
      senderAddress?: string;
      fromDb?: boolean;
      completedChunks?: number;
      totalChunks?: number;
    }> = [];
    const seenIds = new Set<string>();
    
    // Subject of the email currently being viewed — used to scope transfers
    const viewedSubject = emailContent.subject || '';
    
    // 1. In-memory transfers take priority (most current state)
    // Check receive queue for email-initiated transfers
    for (const [sessionId, transfer] of fileTransfer.queuedTransfers.receive) {
      const isEmailTransfer = transfer.metadata?.emailInitiated || (transfer as any).custom?.emailInitiated;
      if (!isEmailTransfer) continue;
      // Subject scoping for in-memory transfers
      const transferSubject = (transfer.metadata as any)?.emailSubject || (transfer as any).custom?.emailSubject;
      if (transferSubject && viewedSubject && transferSubject !== viewedSubject) continue;
      seenIds.add(sessionId);
      transfers.push({
        sessionId,
        fileName: transfer.metadata?.name || 'Unknown file',
        fileSize: transfer.metadata?.size || 0,
        state: transfer.state,
        senderAddress: transfer.metadata?.senderAddress || transfer.metadata?.peerAddress,
        completedChunks: transfer.metadata?.completedChunks || 0,
        totalChunks: transfer.metadata?.totalChunks || 0
      });
    }
    
    // Also check active transfers for email-initiated ones (in progress)
    for (const [sessionId, transfer] of fileTransfer.activeTransfers) {
      const isEmailTransfer = transfer.metadata?.emailInitiated || (transfer as any).custom?.emailInitiated;
      const isInbound = transfer.metadata?.direction === 'inbound';
      if (!isEmailTransfer || !isInbound || seenIds.has(sessionId)) continue;
      // Subject scoping for in-memory transfers
      const transferSubject = (transfer.metadata as any)?.emailSubject || (transfer as any).custom?.emailSubject;
      if (transferSubject && viewedSubject && transferSubject !== viewedSubject) continue;
      seenIds.add(sessionId);
      transfers.push({
        sessionId,
        fileName: transfer.metadata?.name || 'Unknown file',
        fileSize: transfer.metadata?.size || 0,
        state: transfer.state,
        senderAddress: transfer.metadata?.senderAddress || transfer.metadata?.peerAddress,
        completedChunks: transfer.metadata?.completedChunks || 0,
        totalChunks: transfer.metadata?.totalChunks || 0
      });
    }
    
    // 2. Fill in from DB for transfers not yet in memory (post-refresh, post-onboarding)
    for (const dbTransfer of dbEmailTransfers) {
      if (!seenIds.has(dbTransfer.sessionId)) {
        seenIds.add(dbTransfer.sessionId);
        transfers.push({
          sessionId: dbTransfer.sessionId,
          fileName: dbTransfer.fileName,
          fileSize: dbTransfer.fileSize,
          state: dbTransfer.state as FileTransferStateEnum,
          senderAddress: dbTransfer.senderAddress,
          fromDb: true,
        });
      }
    }
    
    // 3. FIRST-OPEN SYNC: Fill in from gateway for transfers not yet in DB or memory
    // These are deferred file deliveries the sender pushed to the gateway.
    // Shown as "Waiting for connection…" until P2P connects and real transfer starts.
    if (transfers.length === 0 && gatewayPendingFiles.length > 0) {
      const seenFileNames = new Set<string>();
      for (const gw of gatewayPendingFiles) {
        const key = `${gw.fileName}:${gw.fileSize}`;
        if (seenFileNames.has(key)) continue;
        seenFileNames.add(key);
        transfers.push({
          sessionId: `gw_${key}`, // Synthetic ID — replaced once real transfer activates
          fileName: gw.fileName,
          fileSize: gw.fileSize,
          state: FileTransferStateEnum.PENDING_CONSENT,
          senderAddress: gw.senderAddress,
          fromDb: true, // Use DB styling (shows "Waiting for peer…" badge)
        });
      }
    }
    
    console.log('[EmailViewPanel] Pending email transfers (merged):', {
      senderEmail,
      inMemory: transfers.filter(t => !t.fromDb).length,
      fromDb: transfers.filter(t => t.fromDb).length,
      fromGateway: gatewayPendingFiles.length,
      total: transfers.length,
      transfers: transfers.map(t => ({ sessionId: t.sessionId.slice(0, 8), fileName: t.fileName, state: t.state, fromDb: !!t.fromDb }))
    });
    
    return transfers;
  }, [emailContent?.from, emailContent?.subject, composeOnly, fileTransfer.queuedTransfers.receive, fileTransfer.activeTransfers, dbEmailTransfers, gatewayPendingFiles]);

  // Handler to start/accept an email P2P transfer
  const handleStartEmailTransfer = useCallback(async (sessionId: string) => {
    console.log('[EmailViewPanel] Starting email P2P transfer:', sessionId.slice(0, 8));
    try {
      await fileTransfer.startTransfer(sessionId);
      toast.success('Transfer started');
    } catch (err) {
      console.error('[EmailViewPanel] Failed to start transfer:', err);
      toast.error('Failed to start transfer');
    }
  }, [fileTransfer]);

  // Load settings and listen for changes
  useEffect(() => {
    const settings = getEmailSettings();
    setLoadImages(settings.loadImages);

    const handleSettingsUpdate = () => {
      const updated = getEmailSettings();
      setLoadImages(updated.loadImages);
    };
    window.addEventListener("email-settings-updated", handleSettingsUpdate);
    return () => window.removeEventListener("email-settings-updated", handleSettingsUpdate);
  }, []);

  // Set default from account when entering compose mode
  useEffect(() => {
    if (composeMode && !composeFromAccountId && accounts.length > 0) {
      // If viewing an email, use that email's account; otherwise use primary
      if (selectedEmail?.account) {
        setComposeFromAccountId(selectedEmail.account.id);
      } else {
        const primary = accounts.find(a => a.isPrimary) || accounts[0];
        setComposeFromAccountId(primary.id);
      }
    }
  }, [composeMode, composeFromAccountId, accounts, selectedEmail]);

  // Debug: track attachment state changes
  useEffect(() => {
    console.log("[EmailViewPanel] composeAttachments changed:", composeAttachments.length, composeAttachments.map(f => f.name));
  }, [composeAttachments]);

  // Debug: track component mount/unmount
  useEffect(() => {
    console.log("[EmailViewPanel] MOUNTED", { composeOnly, selectedEmail: selectedEmail?.id });
    return () => {
      console.log("[EmailViewPanel] UNMOUNTED", { composeOnly, selectedEmail: selectedEmail?.id });
    };
  }, []);

  // Recipient lookup with debounce
  useEffect(() => {
    if (!composeTo || !composeTo.includes("@")) {
      setRecipientLookup({ status: "idle" });
      return;
    }

    if (lookupTimeoutRef.current) {
      clearTimeout(lookupTimeoutRef.current);
    }

    setRecipientLookup({ status: "checking" });

    lookupTimeoutRef.current = setTimeout(async () => {
      const gatewayUrl = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
      if (!gatewayUrl) return;

      try {
        console.log(`[EMAIL_LOOKUP] Looking up recipient: ${composeTo}`);
        const response = await fetch(`${gatewayUrl}/api/user/lookup-email?email=${encodeURIComponent(composeTo)}`);
        const data = await response.json();
        console.log(`[EMAIL_LOOKUP] Response:`, { status: response.status, data });

        if (data.success && data.found && data.user) {
          console.log(`[EMAIL_LOOKUP] Found MobyChat user:`, data.user.publicKey?.slice(0, 8));
          setRecipientLookup({ status: "found", user: data.user });
        } else {
          console.log(`[EMAIL_LOOKUP] Recipient not found on MobyChat`);
          setRecipientLookup({ status: "not_found" });
        }
      } catch (err) {
        console.error(`[EMAIL_LOOKUP] Lookup failed:`, err);
        setRecipientLookup({ status: "not_found" });
      }
    }, 500);

    return () => {
      if (lookupTimeoutRef.current) {
        clearTimeout(lookupTimeoutRef.current);
      }
    };
  }, [composeTo]);

  // Attachment handlers
  const handleAttachFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("[EmailViewPanel] handleAttachFile called", {
      hasFiles: !!e.target.files,
      fileCount: e.target.files?.length,
      composeTo,
      recipientLookupStatus: recipientLookup.status
    });
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      console.log("[EmailViewPanel] Adding files:", newFiles.map(f => f.name));
      setComposeAttachments(prev => {
        const updated = [...prev, ...newFiles];
        console.log("[EmailViewPanel] Updated attachments count:", updated.length);
        return updated;
      });
    }
    e.target.value = "";
  }, [composeTo, recipientLookup.status]);

  const removeAttachment = useCallback((index: number) => {
    setComposeAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Send composed email with optional P2P file transfer
  const handleSendCompose = useCallback(async () => {
    if (!composeTo.trim() || !composeBody.trim()) return;

    const gatewayUrl = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
    if (!gatewayUrl) {
      setError("Email gateway not configured");
      return;
    }

    // Use selected account, or fall back to first account if only one exists
    const account = accounts.find(a => a.id === composeFromAccountId) || (accounts.length === 1 ? accounts[0] : null);
    if (!account) {
      setError("No email account selected");
      return;
    }

    setSending(true);
    const transferIds: string[] = [];

    try {
      let emailBodyText = composeBody;

      // Handle P2P file transfer for attachments (consent-based)
      // Files are queued in PENDING_CONSENT state - transfer starts when receiver accepts
      if (composeAttachments.length > 0 && attachmentRouting === "fileTransfer") {
        console.log("[EmailViewPanel] Preparing P2P file transfers for", composeAttachments.length, "files (consent-based)");
        
        // Check if recipient is on MobyChat
        if (recipientLookup.status === "found" && recipientLookup.user?.publicKey) {
          // Recipient is on MobyChat - prepare for consent-based P2P transfer
          const recipientAddress = recipientLookup.user.publicKey;
          console.log("[EmailViewPanel] Recipient found on MobyChat, preparing consent-based transfer to:", recipientAddress.slice(0, 8));
          
          // Get or create peer connection for transfer preparation
          const peer = node.getOrCreatePeer(recipientAddress);
          
          for (const file of composeAttachments) {
            try {
              // Use consent-based transfer preparation (doesn't auto-transfer)
              const result = await peer.prepareEmailTransfer(
                file,
                recipientAddress,
                composeTo, // email address for tracking
                undefined, // sessionId
                composeSubject || '(no subject)' // emailSubject for scoping
              );
              
              transferIds.push(result.sessionId);
              
              console.log("[EmailViewPanel] File queued for consent-based P2P transfer:", {
                sessionId: result.sessionId.slice(0, 8),
                fileName: file.name,
                fileSize: file.size,
                status: 'PENDING_CONSENT'
              });
            } catch (fileError) {
              console.error("[EmailViewPanel] Error preparing P2P transfer for", file.name, fileError);
              toast.error(`Failed to prepare transfer: ${file.name}`);
            }
          }
          
          // Add transfer info to email body
          if (transferIds.length > 0) {
            const fileList = composeAttachments.map(f => `- ${f.name} (${(f.size / 1024).toFixed(1)} KB)`).join('\n');
            emailBodyText += `\n\n---\n📎 ${transferIds.length} file(s) ready for secure P2P transfer:\n${fileList}\n\nOpen MobyChat and accept the transfer to download these files.\nTransfer is direct peer-to-peer - no cloud storage, fully encrypted.`;
            
            toast.success(`${transferIds.length} file(s) queued for P2P transfer`, {
              description: "Recipient must accept to start download"
            });
          }
        } else {
          // Recipient NOT on MobyChat - use deferred delivery (Node-level OPFS storage)
          // Files are prepared and stored now; auto-delivered when recipient onboards and connects
          console.log("[EmailViewPanel] Recipient not on MobyChat, using deferred delivery");
          
          for (const file of composeAttachments) {
            try {
              const result = await node.prepareEmailTransferDeferred(file, composeTo, composeSubject || '(no subject)');
              transferIds.push(result.sessionId);
              
              console.log("[EmailViewPanel] Deferred delivery prepared:", {
                sessionId: result.sessionId.slice(0, 8),
                fileName: file.name,
                rootHash: result.rootHash.slice(0, 16)
              });
            } catch (fileError) {
              console.error("[EmailViewPanel] Error preparing deferred delivery for", file.name, fileError);
              toast.error(`Failed to prepare deferred transfer: ${file.name}`);
            }
          }
          
          if (transferIds.length > 0) {
            toast.info(`${transferIds.length} file(s) queued for deferred P2P transfer`, {
              description: "Files will be delivered when recipient joins MobyChat"
            });
            
            // Push file metadata to gateway so receiver can discover pending transfers
            // before P2P connection is established (first-open sync)
            if (session?.address && session?.secret) {
              for (const file of composeAttachments) {
                void signedGatewayFetch(
                  `${gatewayUrl}/api/email/pending`,
                  {
                    recipientEmail: composeTo.toLowerCase().trim(),
                    deliveryType: 'file_transfer',
                    metadata: {
                      fileName: file.name,
                      fileSize: file.size,
                      emailSubject: composeSubject || '(no subject)',
                      hasAttachment: true,
                    },
                  },
                  session.address,
                  session.secret
                ).then(r => {
                  if (!r.ok) console.warn('[EmailViewPanel] Failed to push file metadata to gateway:', r.status);
                  else console.log('[EmailViewPanel] Pushed file metadata to gateway for', file.name);
                }).catch(err => {
                  console.warn('[EmailViewPanel] Gateway push error (non-fatal):', err);
                });
              }
            }
          }
        }
      }

      // INLINE ATTACHMENTS: Base64-encode files for standard SMTP delivery
      let smtpAttachments: Array<{ filename: string; content: string }> = [];
      if (composeAttachments.length > 0 && attachmentRouting === "inline") {
        console.log("[EmailViewPanel] Encoding", composeAttachments.length, "files as inline SMTP attachments");
        
        // Client-side size validation (matches server limits: 5MB/file, 10MB total)
        const MAX_PER_FILE = 5 * 1024 * 1024;
        const MAX_TOTAL = 10 * 1024 * 1024;
        let totalSize = 0;
        
        for (const file of composeAttachments) {
          if (file.size > MAX_PER_FILE) {
            toast.error(`${file.name} exceeds 5MB limit for inline attachments. Use P2P Transfer for large files.`);
            setSending(false);
            return;
          }
          totalSize += file.size;
        }
        if (totalSize > MAX_TOTAL) {
          toast.error(`Total attachment size (${(totalSize / (1024 * 1024)).toFixed(1)}MB) exceeds 10MB limit. Use P2P Transfer for large files.`);
          setSending(false);
          return;
        }
        
        // Base64-encode each file
        for (const file of composeAttachments) {
          try {
            const buffer = await file.arrayBuffer();
            const base64 = btoa(
              new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
            );
            smtpAttachments.push({ filename: file.name, content: base64 });
            console.log(`[EmailViewPanel] Encoded ${file.name} (${(file.size / 1024).toFixed(1)}KB)`);
          } catch (encodeErr) {
            console.error(`[EmailViewPanel] Failed to encode ${file.name}:`, encodeErr);
            toast.error(`Failed to encode attachment: ${file.name}`);
            setSending(false);
            return;
          }
        }
        
        console.log(`[EmailViewPanel] ${smtpAttachments.length} attachments encoded, total base64 size: ${(smtpAttachments.reduce((s, a) => s + a.content.length, 0) / 1024).toFixed(0)}KB`);
      }

      // Build email body - use HTML format when attachments present for proper invite/download links
      let finalEmailBody: string;
      let sendAsHtml = false;

      if (composeAttachments.length > 0 && attachmentRouting === "fileTransfer") {
        // Generate share code for invite link (same as contact drawer)
        const contactDetails = session ? createAndSignContactDetails(session) : null;
        const shareCode = contactDetails ? bytesToHex(contactDetails) : '';
        const inviteLink = shareCode ? `${window.location.origin}/chats/#${shareCode}` : `${window.location.origin}/chats/`;
        const inviteCode = '9MOBYCHAT2025INVITE999'; // Onboarding invite code for new users
        const senderName = session?.name || 'Someone';

        // Build file list HTML
        const fileListHtml = composeAttachments.map(f => 
          `<li style="padding: 8px 0; border-bottom: 1px solid #eee;">
            <strong>${f.name}</strong> <span style="color: #666;">(${(f.size / 1024).toFixed(1)} KB)</span>
          </li>`
        ).join('');

        // Build HTML email with proper invite format
        const isRecipientOnMobyChat = recipientLookup.status === "found" && recipientLookup.user?.publicKey;
        
        finalEmailBody = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">

${composeBody ? `<div style="margin-bottom: 24px;">${composeBody.replace(/\n/g, '<br>')}</div>` : ''}

<div style="background-color: #f8f9fa; border-radius: 8px; padding: 20px; margin: 24px 0;">
  <h3 style="margin: 0 0 16px 0; color: #0066cc;">📎 ${composeAttachments.length} Secure File${composeAttachments.length > 1 ? 's' : ''} Attached</h3>
  
  <ul style="list-style: none; padding: 0; margin: 0 0 16px 0;">
    ${fileListHtml}
  </ul>
  
  <p style="margin: 16px 0 8px 0; color: #666; font-size: 14px;">
    ${isRecipientOnMobyChat 
      ? 'Open MobyChat to accept and download these files securely.'
      : `${senderName} wants to share these files with you securely via MobyChat.`}
  </p>
  
  <p><a href="${inviteLink}" style="display: inline-block; padding: 12px 24px; background-color: #0066cc; color: white; text-decoration: none; border-radius: 6px; font-weight: bold;">
    ${isRecipientOnMobyChat ? 'Open MobyChat to Download' : 'Click Here to Connect & Download'}
  </a></p>
  
  ${!isRecipientOnMobyChat ? `
  <p style="margin-top: 16px; color: #666; font-size: 14px;">
    <strong>New to MobyChat?</strong><br>
    Use this Invite Code during signup: <strong style="color: #0066cc;">${inviteCode}</strong>
  </p>
  
  <p style="margin-top: 16px; color: #888; font-size: 12px;">Or manually paste this code in MobyChat:</p>
  <p style="font-family: monospace; background-color: #fff; padding: 12px; border-radius: 4px; word-break: break-all; font-size: 11px; border: 1px solid #ddd;">
    ${shareCode}
  </p>
  ` : ''}
</div>

<p style="color: #888; font-size: 12px;">
  Files transfer directly peer-to-peer when you're both online - no cloud storage, fully encrypted.
</p>

<hr style="border: none; border-top: 1px solid #ddd; margin: 24px 0;">
<p style="color: #888; font-size: 12px;">MobyChat - Secure, decentralized messaging</p>
</body>
</html>`;
        sendAsHtml = true;
      } else {
        // Plain text email (no attachments or inline routing)
        finalEmailBody = emailBodyText;
      }

      // Build credentials based on auth type (OAuth2 vs password)
      // CRITICAL FIX: Previously hardcoded pass:account.password which breaks OAuth accounts
      // OAuth accounts have no password — they use accessToken from Google OAuth flow
      const smtpCredentials: { user: string; pass?: string; accessToken?: string } = {
        user: account.username,
      };
      if (account.authType === "oauth2" && account.accessToken) {
        // Auto-refresh expired OAuth tokens before sending
        const refreshResult = await refreshOAuth2TokenIfNeeded(db, account, gatewayUrl);
        if (refreshResult.status === 'refreshed') {
          account.accessToken = refreshResult.accessToken;
          account.tokenExpiry = refreshResult.tokenExpiry;
          console.log('[EmailViewPanel] Refreshed expired OAuth token before send');
        } else if (refreshResult.status === 'revoked') {
          console.warn('[EmailViewPanel] OAuth token REVOKED:', refreshResult.error);
          toast.error(`OAuth token revoked for ${account.email} — please re-authenticate in Email settings`);
        } else if (refreshResult.status === 'error') {
          console.warn('[EmailViewPanel] OAuth refresh failed (will try with current token):', refreshResult.error);
        }
        smtpCredentials.accessToken = account.accessToken;
      } else {
        smtpCredentials.pass = account.password;
      }

      // Send email via SMTP
      const smtpPayload: Record<string, unknown> = {
        host: account.smtpHost,
        port: account.smtpPort,
        secure: account.smtpPort === 465,
        credentials: smtpCredentials,
        from: account.email,
        to: composeTo,
        subject: composeSubject || "(no subject)",
        body: finalEmailBody,
        html: sendAsHtml,
      };
      
      // Include inline attachments in SMTP payload (base64-encoded)
      if (smtpAttachments.length > 0) {
        smtpPayload.attachments = smtpAttachments;
        console.log(`[EmailViewPanel] Including ${smtpAttachments.length} inline attachment(s) in SMTP payload`);
      }
      
      const response = await fetch(`${gatewayUrl}/api/email/smtp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(smtpPayload),
      });

      // Guard against non-JSON error pages (e.g. nginx 413 returns HTML)
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok && !contentType.includes("application/json")) {
        const hint = response.status === 413
          ? "Payload too large — try a smaller inline attachment or use P2P Transfer."
          : `Server error ${response.status}: ${response.statusText}`;
        console.error("[EmailViewPanel] SMTP send returned non-JSON error", {
          status: response.status,
          statusText: response.statusText,
          contentType
        });
        throw new Error(hint);
      }

      const data = await response.json();

      if (data.success) {
        const hasP2P = transferIds.length > 0;
        const hasInline = smtpAttachments.length > 0;
        const attachSuffix = hasP2P ? ` with ${transferIds.length} P2P transfer(s)` : hasInline ? ` with ${smtpAttachments.length} attachment(s)` : '';
        toast.success(`Email sent to ${composeTo}${attachSuffix}`);
        // Reset compose form
        setComposeTo("");
        setComposeSubject("");
        setComposeBody("");
        setComposeAttachments([]);
        setRecipientLookup({ status: "idle" });
        // Stay in compose mode with fresh/blank fields — user can manually
        // switch to chat panel if desired, but should not be forced out.
      } else {
        setError(data.error || "Failed to send email");
        toast.error(data.error || "Failed to send email");
        // PHANTOM TRANSFER FIX: Clean up P2P transfers that were queued before email send failed
        // Without this, transfers sit in queue with no valid email notification to the receiver,
        // causing chunk errors and progress desync when the system tries to send to a peer
        // who never received the email invitation
        if (transferIds.length > 0) {
          console.warn('[EmailViewPanel] Email send failed — cancelling', transferIds.length, 'phantom P2P transfers');
          for (const sid of transferIds) {
            try {
              const recipientAddress = recipientLookup.status === "found" ? recipientLookup.user?.publicKey : undefined;
              if (recipientAddress) {
                const peer = node.getOrCreatePeer(recipientAddress);
                await peer.cancelTransfer(sid);
              }
            } catch (cancelErr) {
              console.warn('[EmailViewPanel] Failed to cancel phantom transfer', sid, cancelErr);
            }
          }
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to send email";
      setError(errorMsg);
      toast.error(errorMsg);
      // PHANTOM TRANSFER FIX: Same cleanup on exception
      if (transferIds.length > 0) {
        console.warn('[EmailViewPanel] Email send threw — cancelling', transferIds.length, 'phantom P2P transfers');
        for (const sid of transferIds) {
          try {
            const recipientAddress = recipientLookup.status === "found" ? recipientLookup.user?.publicKey : undefined;
            if (recipientAddress) {
              const peer = node.getOrCreatePeer(recipientAddress);
              await peer.cancelTransfer(sid);
            }
          } catch { /* best-effort cleanup */ }
        }
      }
    } finally {
      setSending(false);
    }
  }, [composeTo, composeSubject, composeBody, composeFromAccountId, accounts, composeAttachments, attachmentRouting, recipientLookup, node, session, composeOnly, onBack]);

  const fetchEmailContent = useCallback(async () => {
    if (!selectedEmail) return;
    
    const gatewayUrl = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
    if (!gatewayUrl) {
      setError("Email gateway not configured");
      setLoading(false);
      return;
    }

    const account = selectedEmail.account;
    
    // Build credentials based on auth type
    const credentials: { user: string; pass?: string; accessToken?: string } = {
      user: account.username,
    };
    if (account.authType === "oauth2" && account.accessToken) {
      credentials.accessToken = account.accessToken;
    } else {
      credentials.pass = account.password;
    }

    try {
      const response = await fetch(`${gatewayUrl}/api/email/imap/fetch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host: account.imapHost,
          port: account.imapPort,
          secure: true,
          credentials,
          folder: "INBOX",
          uid: selectedEmail.uid,
        }),
      });

      const data = await response.json();
      
      if (data.success && data.message) {
        setEmailContent(data.message);
        setError(null);
      } else {
        setError(data.error || "Failed to fetch email");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch email");
    } finally {
      setLoading(false);
    }
  }, [selectedEmail]);

  useEffect(() => {
    if (!composeOnly && selectedEmail) {
      fetchEmailContent();
    }
  }, [fetchEmailContent, composeOnly, selectedEmail]);

  const handleReply = useCallback(async () => {
    if (!emailContent || !replyText.trim() || !selectedEmail) return;
    
    const gatewayUrl = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
    if (!gatewayUrl) {
      setError("Email gateway not configured");
      return;
    }

    const account = selectedEmail.account;
    setSending(true);

    try {
      const response = await fetch(`${gatewayUrl}/api/email/smtp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host: account.smtpHost,
          port: account.smtpPort,
          secure: account.smtpPort === 465,
          credentials: {
            user: account.username,
            pass: account.password,
          },
          from: account.email,
          to: emailContent.from,
          subject: emailContent.subject.startsWith("Re:") 
            ? emailContent.subject 
            : `Re: ${emailContent.subject}`,
          text: replyText,
          inReplyTo: emailContent.messageId,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setReplyMode(false);
        setReplyText("");
        // Could show a success toast here
      } else {
        setError(data.error || "Failed to send reply");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reply");
    } finally {
      setSending(false);
    }
  }, [emailContent, replyText, selectedEmail]);

  // State for delete confirmation
  const [deleting, setDeleting] = useState(false);

  const handleForward = useCallback(() => {
    if (!emailContent) return;
    setComposeMode(true);
    setReplyMode(false);
    setComposeTo("");
    setComposeSubject(
      emailContent.subject.startsWith("Fwd:")
        ? emailContent.subject
        : `Fwd: ${emailContent.subject}`
    );
    const fwdHeader = [
      "---------- Forwarded message ----------",
      `From: ${emailContent.fromName ? `${emailContent.fromName} <${emailContent.from}>` : emailContent.from}`,
      `Date: ${emailContent.date}`,
      `Subject: ${emailContent.subject}`,
      `To: ${emailContent.to.map(t => t.name || t.address).join(", ")}`,
      "",
    ].join("\n");
    setComposeBody(`\n\n${fwdHeader}\n${emailContent.body || ""}`);
  }, [emailContent]);

  const handleDelete = useCallback(async () => {
    if (!selectedEmail || deleting) return;

    // Optimistic local removal — remove from UI immediately
    window.dispatchEvent(new CustomEvent("email-deleted", { detail: { id: selectedEmail.id } }));
    toast.success("Email removed");
    onBack();

    // Attempt server-side IMAP delete in the background (best-effort)
    const gatewayUrl = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
    if (!gatewayUrl) return;

    const account = selectedEmail.account;
    const credentials: { user: string; pass?: string; accessToken?: string } = { user: account.username };
    if (account.authType === "oauth2" && account.accessToken) {
      credentials.accessToken = account.accessToken;
    } else {
      credentials.pass = account.password;
    }

    try {
      const response = await fetch(`${gatewayUrl}/api/email/imap/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host: account.imapHost,
          port: account.imapPort,
          secure: true,
          credentials,
          folder: "INBOX",
          uid: selectedEmail.uid,
        }),
      });
      if (!response.ok) {
        console.warn("[EmailViewPanel] Server-side delete returned", response.status, "(gateway route may not exist yet)");
        return;
      }
      const data = await response.json();
      if (!data.success) {
        console.warn("[EmailViewPanel] Server-side delete failed:", data.error);
      }
    } catch (err) {
      console.warn("[EmailViewPanel] Server-side delete error (email removed locally):", err);
    }
  }, [selectedEmail, deleting, onBack]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Compose-only mode - show full-screen compose UI
  if (composeOnly) {
    return (
      <div className="bg-surface rounded-t-large sm:rounded-tr-large relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-tr-none sm:mr-6">
        {/* Header - matches ChatPanel header style */}
        <div className="bg-surface-container-highest rounded-t-large sm:rounded-tr-large flex min-w-0 shrink-0 items-center gap-3 rounded-tr-none p-4">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-on-surface/8 transition-colors shrink-0"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5 text-on-surface" />
          </button>
          <PenSquare className="w-5 h-5 text-primary shrink-0" />
          <h2 className="text-title-lg text-on-surface">New Email</h2>
        </div>

        {/* Compose form - full height */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4 max-w-2xl">
            {/* From account selector */}
            {accounts.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="text-label-sm text-on-surface-variant w-16">From</label>
                <select
                  value={composeFromAccountId}
                  onChange={(e) => setComposeFromAccountId(e.target.value)}
                  className="flex-1 px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-md text-on-surface focus:outline-none focus:border-primary"
                  title="Select sender email account"
                >
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.email} {acc.isPrimary ? "(Primary)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* To field with recipient lookup */}
            <div className="flex items-center gap-3">
              <label className="text-label-sm text-on-surface-variant w-16">To</label>
              <div className="flex-1 relative">
                <input
                  type="email"
                  value={composeTo}
                  onChange={(e) => setComposeTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="w-full px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-md text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary pr-28"
                  autoFocus
                />
                {recipientLookup.status !== "idle" && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {recipientLookup.status === "checking" && (
                      <span className="text-label-sm text-on-surface-variant">Checking...</span>
                    )}
                    {recipientLookup.status === "found" && (
                      <span className="text-label-sm px-2 py-0.5 rounded bg-tertiary/20 text-tertiary">
                        MobyChat ✓
                      </span>
                    )}
                    {recipientLookup.status === "not_found" && (
                      <span className="text-label-sm px-2 py-0.5 rounded bg-surface-container-highest text-on-surface-variant">
                        External
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Subject */}
            <div className="flex items-center gap-3">
              <label className="text-label-sm text-on-surface-variant w-16">Subject</label>
              <input
                type="text"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="Subject"
                className="flex-1 px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-md text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary"
              />
            </div>

            {/* Message body */}
            <textarea
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              placeholder="Write your message..."
              className="w-full min-h-[300px] p-4 rounded-lg bg-surface-container border border-outline-variant text-on-surface resize-none focus:outline-none focus:border-primary text-body-md"
            />

            {/* Attachments */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-label-md text-on-surface-variant">Attachments</span>
                <label className="text-label-md text-primary cursor-pointer hover:underline">
                  + Add File
                  <input
                    type="file"
                    multiple
                    onChange={handleAttachFile}
                    className="hidden"
                  />
                </label>
              </div>

              {composeAttachments.length > 0 && (
                <div className="space-y-2">
                  {composeAttachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-surface-container rounded-lg border border-outline-variant"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Paperclip className="w-5 h-5 text-primary flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-body-md truncate">{file.name}</div>
                          <div className="text-label-sm text-on-surface-variant">
                            {(file.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeAttachment(index)}
                        className="text-on-surface-variant hover:text-error p-1"
                        title="Remove attachment"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}

                  {/* Routing options */}
                  <div className="p-4 rounded-lg bg-surface-container-highest border border-outline-variant">
                    <div className="text-label-md text-on-surface-variant mb-3">Attachment Routing</div>
                    <div className="flex items-center gap-2 mb-3">
                      <button
                        onClick={() => setAttachmentRouting("fileTransfer")}
                        className={`flex-1 text-label-md px-4 py-2 rounded-lg ${
                          attachmentRouting === "fileTransfer"
                            ? "bg-primary text-on-primary"
                            : "bg-surface text-on-surface-variant"
                        }`}
                      >
                        P2P Transfer
                      </button>
                      <button
                        onClick={() => setAttachmentRouting("inline")}
                        className={`flex-1 text-label-md px-4 py-2 rounded-lg ${
                          attachmentRouting === "inline"
                            ? "bg-primary text-on-primary"
                            : "bg-surface text-on-surface-variant"
                        }`}
                      >
                        Inline (SMTP)
                      </button>
                    </div>

                    {attachmentRouting === "fileTransfer" && (
                      <div className="text-body-sm">
                        {recipientLookup.status === "found" ? (
                          <div className="flex items-center gap-2 text-tertiary">
                            <span>✓</span>
                            <span>
                              Direct P2P transfer to {recipientLookup.user?.preferredName || "MobyChat user"}
                              {recipientLookup.user?.presence?.status === "online" && (
                                <span className="ml-1 text-green-500">(online)</span>
                              )}
                            </span>
                          </div>
                        ) : recipientLookup.status === "not_found" ? (
                          <div className="text-on-surface-variant">
                            <div className="flex items-center gap-2 mb-1">
                              <span>⚠</span>
                              <span>Recipient not on MobyChat</span>
                            </div>
                            <div className="text-label-sm">
                              An invite link will be included. Files transfer when they join.
                            </div>
                          </div>
                        ) : (
                          <div className="text-on-surface-variant">
                            Enter recipient email to check routing
                          </div>
                        )}
                      </div>
                    )}

                    {attachmentRouting === "inline" && (
                      <div className="text-body-sm text-on-surface-variant">
                        Files sent via traditional email (size limits apply)
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="px-4 py-3 bg-error/10 text-error text-body-sm rounded-lg">
                {error}
              </div>
            )}

            {/* Send button */}
            <div className="flex justify-end pt-4">
              <button
                onClick={handleSendCompose}
                disabled={!composeTo.trim() || !composeBody.trim() || sending}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-on-primary rounded-full text-label-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-on-primary" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-surface rounded-t-large sm:rounded-tr-large relative flex min-w-0 flex-1 flex-col items-center justify-center overflow-x-hidden rounded-tr-none sm:mr-6">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <span className="text-on-surface-variant">Loading email...</span>
        </div>
      </div>
    );
  }

  if (error && !emailContent) {
    return (
      <div className="bg-surface rounded-t-large sm:rounded-tr-large relative flex min-w-0 flex-1 flex-col items-center justify-center overflow-x-hidden rounded-tr-none sm:mr-6">
        <div className="text-center space-y-4 max-w-md px-8">
          <div className="bg-error/12 rounded-full p-6 mx-auto w-24 h-24 flex items-center justify-center">
            <MailIcon className="w-12 h-12 text-error" />
          </div>
          <div>
            <h3 className="text-title-lg text-on-surface mb-2">Failed to load email</h3>
            <p className="text-body-md text-on-surface-variant">{error}</p>
          </div>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-primary text-on-primary rounded-full text-label-lg"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-t-large sm:rounded-tr-large relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-tr-none sm:mr-6">
      {/* Header - matches ChatPanel header style */}
      <div className="bg-surface-container-highest rounded-t-large sm:rounded-tr-large flex min-w-0 shrink-0 items-center justify-between gap-3 rounded-tr-none p-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-on-surface/8 transition-colors shrink-0"
            title="Back to email list"
          >
            <ArrowLeft className="w-5 h-5 text-on-surface" />
          </button>
          {selectedEmail && (
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: selectedEmail.accountColor }}
              title={selectedEmail.account.email}
            />
          )}
          <div className="min-w-0">
            <h2 className="text-title-lg -mb-1 block truncate">
              {emailContent?.subject || selectedEmail?.subject || "(no subject)"}
            </h2>
            <code className="bg-surface-container text-on-surface rounded-extra-small truncate px-1 py-0.5 text-xs">
              {emailContent?.from || selectedEmail?.from || ""}
            </code>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1">
          <button
            onClick={() => {
              setComposeMode(!composeMode);
              if (!composeMode) setReplyMode(false);
            }}
            className={`p-2 rounded-full transition-colors ${
              composeMode ? "bg-primary text-on-primary" : "hover:bg-on-surface/8"
            }`}
            title="Compose new email"
          >
            <PenSquare className="w-5 h-5" />
          </button>
          <button
            onClick={() => {
              setReplyMode(!replyMode);
              if (!replyMode) setComposeMode(false);
            }}
            className={`p-2 rounded-full transition-colors ${
              replyMode ? "bg-primary text-on-primary" : "hover:bg-on-surface/8"
            }`}
            title="Reply"
          >
            <Reply className="w-5 h-5" />
          </button>
          <button
            onClick={handleForward}
            disabled={!emailContent}
            className={`p-2 rounded-full transition-colors ${emailContent ? "hover:bg-on-surface/8" : "opacity-50"}`}
            title="Forward"
          >
            <Forward className="w-5 h-5 text-on-surface" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting || !selectedEmail}
            className={`p-2 rounded-full transition-colors ${deleting ? "opacity-50" : "hover:bg-error/10 text-error"}`}
            title="Delete email"
          >
            {deleting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Trash2 className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Email metadata */}
      <div className="px-4 py-3 border-b border-outline-variant bg-surface-container-low">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <span className="text-primary text-title-md">
              {(emailContent?.fromName || emailContent?.from || "?")[0].toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-body-lg font-medium text-on-surface">
                {emailContent?.fromName || emailContent?.from}
              </span>
              {emailContent?.fromName && (
                <span className="text-body-sm text-on-surface-variant">
                  &lt;{emailContent.from}&gt;
                </span>
              )}
            </div>
            <div className="text-body-sm text-on-surface-variant">
              To: {emailContent?.to.map(t => t.name || t.address).join(", ")}
            </div>
            {emailContent?.cc && emailContent.cc.length > 0 && (
              <div className="text-body-sm text-on-surface-variant">
                Cc: {emailContent.cc.map(c => c.name || c.address).join(", ")}
              </div>
            )}
            <div className="text-body-sm text-on-surface-variant mt-1">
              {emailContent?.date ? formatDate(emailContent.date) : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-error/10 text-error text-body-sm border-b border-error/20">
          {error}
        </div>
      )}

      {/* IMAP Inline Attachments */}
      {emailContent?.attachments && emailContent.attachments.length > 0 && (
        <div className="px-4 py-3 border-b border-outline-variant bg-surface-container-low">
          <div className="flex items-center gap-2 mb-3">
            <Paperclip className="w-4 h-4 text-on-surface-variant" />
            <span className="text-body-sm font-medium text-on-surface-variant">
              {emailContent.attachments.length} Attachment{emailContent.attachments.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-2">
            {emailContent.attachments.map((att) => {
              const isDownloading = downloadingParts.has(att.partIndex);
              const sizeStr = att.size >= 1024 * 1024
                ? `${(att.size / (1024 * 1024)).toFixed(1)} MB`
                : `${(att.size / 1024).toFixed(1)} KB`;
              return (
                <div
                  key={att.partIndex}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-container border border-outline-variant"
                >
                  <FileIcon className="w-5 h-5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-body-sm font-medium text-on-surface truncate">
                      {att.filename}
                    </div>
                    <div className="text-label-sm text-on-surface-variant">
                      {sizeStr} &middot; {att.contentType}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadAttachment(att)}
                    disabled={isDownloading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-on-primary text-label-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 shrink-0"
                  >
                    {isDownloading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    {isDownloading ? 'Downloading...' : 'Download'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legacy attachment indicator (no inline data) */}
      {selectedEmail?.hasAttachment && (!emailContent?.attachments || emailContent.attachments.length === 0) && (
        <div className="px-4 py-3 border-b border-outline-variant bg-surface-container-low">
          <div className="flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-on-surface-variant" />
            <span className="text-body-sm text-on-surface-variant">
              This email has attachments
            </span>
          </div>
        </div>
      )}

      {/* P2P Secure File Transfers - Native UI (outside iframe) */}
      {pendingEmailTransfers.length > 0 && (
        <div className="px-4 py-3 border-b border-outline-variant bg-tertiary/5">
          <div className="flex items-center gap-2 mb-3">
            <Download className="w-4 h-4 text-tertiary" />
            <span className="text-body-sm font-medium text-tertiary">
              {pendingEmailTransfers.length} Secure File{pendingEmailTransfers.length > 1 ? 's' : ''} Attached
            </span>
          </div>
          <div className="space-y-2">
            {pendingEmailTransfers.map((transfer) => {
              const isCompleted = transfer.state === FileTransferStateEnum.COMPLETED;
              const isFailed = transfer.state === FileTransferStateEnum.FAILED;
              const isTransferring = transfer.state === FileTransferStateEnum.TRANSFERRING ||
                                    transfer.state === FileTransferStateEnum.METADATA_PENDING ||
                                    transfer.state === FileTransferStateEnum.METADATA_SENT;
              const isQueued = transfer.state === FileTransferStateEnum.IDLE ||
                              transfer.state === FileTransferStateEnum.METADATA_RECEIVED ||
                              transfer.state === FileTransferStateEnum.READY_FOR_TRANSFER ||
                              transfer.state === FileTransferStateEnum.PENDING_CONSENT;
              // DB-only records have no in-memory transfer to interact with
              const isDbOnly = !!(transfer as any).fromDb;
              
              return (
                <div
                  key={transfer.sessionId}
                  className="flex items-center justify-between p-3 rounded-lg border bg-surface shadow-sm"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0 ${
                      isCompleted ? 'bg-green-500/10' : isFailed ? 'bg-error/10' : 'bg-tertiary/10'
                    }`}>
                      <FileIcon className={`h-5 w-5 ${
                        isCompleted ? 'text-green-600' : isFailed ? 'text-error' : 'text-tertiary'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-body-sm font-medium text-on-surface truncate">
                        {transfer.fileName}
                      </div>
                      <div className="text-label-sm text-on-surface-variant">
                        {transfer.fileSize >= 1048576 
                          ? `${(transfer.fileSize / 1048576).toFixed(1)} MB`
                          : `${(transfer.fileSize / 1024).toFixed(1)} KB`} • P2P Transfer
                      </div>
                    </div>
                  </div>
                  
                  {isCompleted ? (
                    <span className="text-label-sm text-green-600 px-3 py-1 bg-green-500/10 rounded-full font-medium flex-shrink-0">
                      ✓ Complete
                    </span>
                  ) : isFailed ? (
                    <span className="text-label-sm text-error px-3 py-1 bg-error/10 rounded-full flex-shrink-0">
                      Failed
                    </span>
                  ) : isQueued && !isDbOnly ? (
                    <button
                      onClick={() => handleStartEmailTransfer(transfer.sessionId)}
                      className="flex items-center gap-2 px-4 py-2 bg-tertiary text-on-tertiary rounded-full text-label-md font-medium hover:bg-tertiary/90 transition-colors flex-shrink-0"
                    >
                      <Play className="w-4 h-4" />
                      Start Download
                    </button>
                  ) : isTransferring && !isDbOnly ? (
                    <div className="flex items-center gap-2 px-4 py-2 text-tertiary">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-label-md">
                        {transfer.totalChunks && transfer.totalChunks > 0
                          ? `${Math.round(((transfer.completedChunks || 0) / transfer.totalChunks) * 100)}%`
                          : 'Transferring...'}
                      </span>
                    </div>
                  ) : isDbOnly ? (
                    <span className="text-label-sm text-on-surface-variant px-3 py-1 bg-surface-container rounded-full flex-shrink-0">
                      Waiting for peer…
                    </span>
                  ) : (
                    <span className="text-label-sm text-on-surface-variant px-3 py-1 bg-surface-container rounded">
                      {transfer.state}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 text-label-sm text-on-surface-variant">
            Files transfer directly peer-to-peer when you're both online - no cloud storage.
          </div>
        </div>
      )}

      {/* Email body */}
      <div className="flex-1 min-w-0 basis-0 overflow-y-auto overflow-x-hidden p-4">
        {loadImages && emailContent?.htmlBody ? (
          // Render HTML in sandboxed iframe for security
          <iframe
            ref={iframeRef}
            srcDoc={sanitizeHtmlForIframe(emailContent.htmlBody, true)}
            className="w-full max-w-full min-h-[400px] border-0 bg-white rounded-lg"
            sandbox="allow-same-origin"
            title="Email content"
            onLoad={() => {
              if (!iframeRef.current?.contentDocument) return;
              
              // Auto-resize iframe to content height
              const height = iframeRef.current.contentDocument.body.scrollHeight;
              iframeRef.current.style.height = `${Math.max(400, height + 20)}px`;
              
              // Intercept all link clicks inside the sandboxed iframe
              // Without this, clicks navigate inside the sandbox causing blank renders
              const links = iframeRef.current.contentDocument.querySelectorAll('a[href]');
              links.forEach((link) => {
                link.addEventListener('click', (e) => {
                  e.preventDefault();
                  const href = (link as HTMLAnchorElement).href;
                  if (!href || href === '#') return;
                  
                  // MobyChat invite/download links — extract share code and navigate via hash
                  // AddContactDialog reads useLocationHash() and opens the contact import dialog
                  // Matches both /chats/#<hex> and /chats/?invite=CODE#<hex> formats
                  const chatHashMatch = href.match(/\/chats\/[^#]*#([a-fA-F0-9]+)/);
                  if (chatHashMatch) {
                    console.log('[EmailViewPanel] Intercepted MobyChat link, navigating to contact import', {
                      shareCodePrefix: chatHashMatch[1].slice(0, 16)
                    });
                    window.location.hash = `#${chatHashMatch[1]}`;
                    return;
                  }
                  
                  // All other links — open in new tab
                  window.open(href, '_blank', 'noopener,noreferrer');
                });
              });
            }}
          />
        ) : emailContent?.htmlBody ? (
          // HTML email but images disabled - render in sandboxed iframe to isolate email CSS
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-body-sm text-on-surface-variant bg-surface-container-low rounded-lg p-2">
              <ImageOff className="w-4 h-4" />
              <span>HTML content rendered without images (enable in Email Settings)</span>
            </div>
            <iframe
              ref={iframeRef}
              srcDoc={sanitizeHtmlForIframe(emailContent.htmlBody, false)}
              className="w-full max-w-full min-h-[400px] border-0 bg-white rounded-lg"
              sandbox="allow-same-origin"
              title="Email content (images disabled)"
              onLoad={() => {
                if (!iframeRef.current?.contentDocument) return;
                const height = iframeRef.current.contentDocument.body.scrollHeight;
                iframeRef.current.style.height = `${Math.max(400, height + 20)}px`;

                const links = iframeRef.current.contentDocument.querySelectorAll('a[href]');
                links.forEach((link) => {
                  link.addEventListener('click', (e) => {
                    e.preventDefault();
                    const href = (link as HTMLAnchorElement).href;
                    if (!href || href === '#') return;
                    const chatHashMatch = href.match(/\/chats\/[^#]*#([a-fA-F0-9]+)/);
                    if (chatHashMatch) {
                      window.location.hash = `#${chatHashMatch[1]}`;
                      return;
                    }
                    window.open(href, '_blank', 'noopener,noreferrer');
                  });
                });
              }}
            />
          </div>
        ) : emailContent?.body && looksLikeRawMime(emailContent.body) ? (
          // Raw MIME detected — extract readable content client-side
          (() => {
            const extracted = extractReadableBody(emailContent.body);
            if (extracted.html) {
              return (
                <iframe
                  ref={iframeRef}
                  srcDoc={sanitizeHtmlForIframe(extracted.html, loadImages)}
                  className="w-full max-w-full min-h-[400px] border-0 bg-white rounded-lg"
                  sandbox="allow-same-origin"
                  title="Email content (extracted from MIME)"
                  onLoad={() => {
                    if (!iframeRef.current?.contentDocument) return;
                    const height = iframeRef.current.contentDocument.body.scrollHeight;
                    iframeRef.current.style.height = `${Math.max(400, height + 20)}px`;
                  }}
                />
              );
            }
            return (
              <pre className="whitespace-pre-wrap font-sans text-body-md text-on-surface">
                {extracted.text}
              </pre>
            );
          })()
        ) : (
          <pre className="whitespace-pre-wrap font-sans text-body-md text-on-surface">
            {emailContent?.body || "No content"}
          </pre>
        )}
      </div>

      {/* Reply composer */}
      {replyMode && (
        <div className="border-t border-outline-variant p-4 bg-surface-container-low">
          <div className="flex items-center gap-2 mb-2">
            <Reply className="w-4 h-4 text-on-surface-variant" />
            <span className="text-body-sm text-on-surface-variant">
              Replying to {emailContent?.fromName || emailContent?.from}
            </span>
            <button
              onClick={() => setReplyMode(false)}
              className="ml-auto p-1 rounded-full hover:bg-on-surface/8"
              title="Cancel reply"
            >
              <X className="w-4 h-4 text-on-surface-variant" />
            </button>
          </div>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write your reply..."
            className="w-full min-h-[120px] p-3 rounded-lg bg-surface border border-outline-variant text-on-surface resize-none focus:outline-none focus:border-primary"
            autoFocus
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handleReply}
              disabled={!replyText.trim() || sending}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded-full text-label-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-on-primary" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Reply
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Compose new email */}
      {composeMode && (
        <div className="border-t border-outline-variant p-4 bg-surface-container-low max-h-[60vh] overflow-y-auto">
          <div className="flex items-center gap-2 mb-4">
            <PenSquare className="w-4 h-4 text-on-surface-variant" />
            <span className="text-body-sm font-medium text-on-surface">New Email</span>
            <button
              onClick={() => setComposeMode(false)}
              className="ml-auto p-1 rounded-full hover:bg-on-surface/8"
              title="Cancel"
            >
              <X className="w-4 h-4 text-on-surface-variant" />
            </button>
          </div>

          <div className="space-y-3">
            {/* From account selector */}
            {accounts.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-label-sm text-on-surface-variant w-16">From</label>
                {accounts.length === 1 ? (
                  <div className="flex-1 px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-sm text-on-surface">
                    {accounts[0].email}
                  </div>
                ) : (
                  <select
                    value={composeFromAccountId}
                    onChange={(e) => setComposeFromAccountId(e.target.value)}
                    className="flex-1 px-3 py-2 bg-surface border border-outline-variant rounded-lg text-body-sm text-on-surface focus:outline-none focus:border-primary"
                    title="Select sender email account"
                  >
                    {accounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        {acc.email} {acc.isPrimary ? "(Primary)" : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* To field with recipient lookup */}
            <div className="flex items-center gap-2">
              <label className="text-label-sm text-on-surface-variant w-16">To</label>
              <div className="flex-1 relative">
                <input
                  type="email"
                  value={composeTo}
                  onChange={(e) => setComposeTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary pr-24"
                  autoFocus
                />
                {recipientLookup.status !== "idle" && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    {recipientLookup.status === "checking" && (
                      <span className="text-label-sm text-on-surface-variant">Checking...</span>
                    )}
                    {recipientLookup.status === "found" && (
                      <span className="text-label-sm px-2 py-0.5 rounded bg-tertiary/20 text-tertiary">
                        MobyChat ✓
                      </span>
                    )}
                    {recipientLookup.status === "not_found" && (
                      <span className="text-label-sm px-2 py-0.5 rounded bg-surface-container-highest text-on-surface-variant">
                        External
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Subject */}
            <div className="flex items-center gap-2">
              <label className="text-label-sm text-on-surface-variant w-16">Subject</label>
              <input
                type="text"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="Subject"
                className="flex-1 px-3 py-2 bg-surface border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary"
              />
            </div>

            {/* Message body */}
            <textarea
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              placeholder="Write your message..."
              className="w-full min-h-[150px] p-3 rounded-lg bg-surface border border-outline-variant text-on-surface resize-none focus:outline-none focus:border-primary"
            />

            {/* Attachments */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-label-sm text-on-surface-variant">Attachments</span>
                <label className="text-label-sm text-primary cursor-pointer hover:underline">
                  + Add File
                  <input
                    type="file"
                    multiple
                    onChange={handleAttachFile}
                    className="hidden"
                  />
                </label>
              </div>

              {composeAttachments.length > 0 && (
                <div className="space-y-2">
                  {composeAttachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-surface rounded-lg border border-outline-variant"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip className="w-4 h-4 text-primary flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-body-sm truncate">{file.name}</div>
                          <div className="text-label-sm text-on-surface-variant">
                            {(file.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeAttachment(index)}
                        className="text-on-surface-variant hover:text-error p-1"
                        title="Remove attachment"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {/* Routing options */}
                  <div className="p-3 rounded-lg bg-surface-container-highest border border-outline-variant">
                    <div className="text-label-sm text-on-surface-variant mb-2">Attachment Routing</div>
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => setAttachmentRouting("fileTransfer")}
                        className={`flex-1 text-label-sm px-3 py-1.5 rounded ${
                          attachmentRouting === "fileTransfer"
                            ? "bg-primary text-on-primary"
                            : "bg-surface text-on-surface-variant"
                        }`}
                      >
                        P2P Transfer
                      </button>
                      <button
                        onClick={() => setAttachmentRouting("inline")}
                        className={`flex-1 text-label-sm px-3 py-1.5 rounded ${
                          attachmentRouting === "inline"
                            ? "bg-primary text-on-primary"
                            : "bg-surface text-on-surface-variant"
                        }`}
                      >
                        Inline (SMTP)
                      </button>
                    </div>

                    {attachmentRouting === "fileTransfer" && (
                      <div className="text-body-sm">
                        {recipientLookup.status === "found" ? (
                          <div className="flex items-center gap-2 text-tertiary">
                            <span>✓</span>
                            <span>
                              Direct P2P transfer to {recipientLookup.user?.preferredName || "MobyChat user"}
                              {recipientLookup.user?.presence?.status === "online" && (
                                <span className="ml-1 text-green-500">(online)</span>
                              )}
                            </span>
                          </div>
                        ) : recipientLookup.status === "not_found" ? (
                          <div className="text-on-surface-variant">
                            <div className="flex items-center gap-2 mb-1">
                              <span>⚠</span>
                              <span>Recipient not on MobyChat</span>
                            </div>
                            <div className="text-label-sm">
                              An invite link will be included. Files transfer when they join.
                            </div>
                          </div>
                        ) : (
                          <div className="text-on-surface-variant">
                            Enter recipient email to check routing
                          </div>
                        )}
                      </div>
                    )}

                    {attachmentRouting === "inline" && (
                      <div className="text-body-sm text-on-surface-variant">
                        Files sent via traditional email (size limits apply)
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Send button */}
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSendCompose}
                disabled={!composeTo.trim() || !composeBody.trim() || sending}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded-full text-label-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-on-primary" />
                    {prepProgress && prepProgress.phase !== 'complete' ? (
                      prepProgress.phase === 'merkle'
                        ? `Building tree... ${prepProgress.progress}%`
                        : prepProgress.phase === 'opfs'
                        ? `Writing to storage... ${prepProgress.progress}%`
                        : prepProgress.phase === 'preparing'
                        ? 'Preparing...'
                        : 'Sending...'
                    ) : (
                      'Sending...'
                    )}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
